'use client';

import { Button } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import { useState } from 'react';

import { BlockConfirmModal } from '@/modules/players/v3/components/shared/friend-modal/block-confirm-modal';
import { DeclineConfirmModal } from '@/modules/players/v3/components/shared/friend-modal/decline-confirm-modal';
import { RevokeConfirmModal } from '@/modules/players/v3/components/shared/friend-modal/revoke-confirm-modal';
import { UnfriendConfirmModal } from '@/modules/players/v3/components/shared/friend-modal/unfriend-confirm-modal';
import { useFriendActions } from '@/modules/players/v3/hooks/use-friend-actions';
import { useGetRelationship } from '@/modules/players/v3/hooks/use-get-relationship';
import { FriendCardModalType, FriendCardType } from '@/modules/players/v3/types/friend';
import type { Friend } from '@/types/friends.types';

interface FriendActionButtonsProps {
  playerUuid: string;
  userUuid: string;
  playerFirstName: string;
  playerLastName: string;
  playerSlug: string;
  playerProfileImage?: string;
  size?: 'sm' | 'lg';
}

export const FriendActionButtons = ({
  playerUuid,
  userUuid,
  playerFirstName,
  playerLastName,
  playerSlug,
  playerProfileImage,
  size = 'lg',
}: FriendActionButtonsProps) => {
  const { data: relationship, isLoading } = useGetRelationship({
    userUuid,
    targetUuid: playerUuid,
  });

  const {
    onBlock,
    onUnfriend,
    onAcceptFriendRequest,
    onDeclineFriendRequest,
    onRevokeFriendRequest,
    onSendFriendRequest,
  } = useFriendActions(userUuid);

  const [modalType, setModalType] = useState<FriendCardModalType | null>(null);

  const handleOpenModal = (type: FriendCardModalType) => setModalType(type);
  const handleCloseModal = () => setModalType(null);

  const isSmall = size === 'sm';
  const primaryClassName = clsx(
    isSmall && '!rounded !px-2 !py-1 !text-sm whitespace-nowrap bg-experiment',
    !isSmall && 'w-full flex items-center justify-center'
  );
  const secondaryClassName = clsx(
    isSmall &&
      '!rounded !px-2 !py-1 !text-sm text-purple-600 hover:!text-purple-700 whitespace-nowrap',
    !isSmall && 'w-full flex items-center justify-center'
  );
  const containerClassName = clsx('flex items-center', !isSmall && 'justify-center w-full');
  const buttonGroupClassName = clsx('flex gap-2', !isSmall && 'w-full');

  const profileFriend: Friend = {
    uuid: playerUuid,
    slug: playerSlug,
    firstName: playerFirstName,
    lastName: playerLastName,
    nameSuffix: '',
    profileImage: playerProfileImage ?? '',
    city: '',
    state: '',
    country: '',
    stateId: 0,
    countryId: 0,
    stateAbbreviation: '',
    countryAbbreviation: '',
    fullName: `${playerFirstName} ${playerLastName}`,
  };

  if (isLoading) {
    return (
      <div className={containerClassName} aria-hidden>
        <div
          className={clsx(
            'animate-pulse rounded bg-gray-200',
            isSmall ? 'h-[28px] w-[90px]' : 'h-11 w-[117px] md:w-[192px]'
          )}
          aria-hidden
        />
      </div>
    );
  }

  const type = relationship?.friendType ?? FriendCardType.SUGGESTION;

  return (
    <div className={containerClassName}>
      {type === FriendCardType.SUGGESTION && (
        <Button
          variant='primary'
          size={size}
          className={primaryClassName}
          onClick={() => onSendFriendRequest(profileFriend)}
        >
          Add friend
        </Button>
      )}

      {type === FriendCardType.FRIEND && (
        <div className={buttonGroupClassName}>
          <Button
            variant='secondary'
            size={size}
            className={secondaryClassName}
            onClick={() => handleOpenModal(FriendCardModalType.BLOCK)}
          >
            Block
          </Button>
          <Button
            variant='primary'
            size={size}
            className={primaryClassName}
            onClick={() => handleOpenModal(FriendCardModalType.UNFRIEND)}
          >
            Unfriend
          </Button>
        </div>
      )}

      {type === FriendCardType.REQUEST && (
        <div className={buttonGroupClassName}>
          <Button
            variant='secondary'
            size={size}
            className={secondaryClassName}
            onClick={() => handleOpenModal(FriendCardModalType.DECLINE)}
          >
            Decline
          </Button>
          <Button
            variant='primary'
            size={size}
            className={primaryClassName}
            onClick={() => onAcceptFriendRequest(profileFriend)}
          >
            Accept
          </Button>
        </div>
      )}

      {type === FriendCardType.PENDING && (
        <Button
          variant='secondary'
          size={size}
          className={secondaryClassName}
          onClick={() => handleOpenModal(FriendCardModalType.REVOKE)}
        >
          Cancel request
        </Button>
      )}

      <BlockConfirmModal
        open={modalType === FriendCardModalType.BLOCK}
        onOpenChange={handleCloseModal}
        onConfirm={() => {
          handleCloseModal();
          onBlock(profileFriend);
        }}
      />
      <UnfriendConfirmModal
        open={modalType === FriendCardModalType.UNFRIEND}
        onOpenChange={handleCloseModal}
        onConfirm={() => {
          handleCloseModal();
          onUnfriend(profileFriend);
        }}
      />
      <DeclineConfirmModal
        open={modalType === FriendCardModalType.DECLINE}
        onOpenChange={handleCloseModal}
        onConfirm={() => {
          handleCloseModal();
          onDeclineFriendRequest(profileFriend);
        }}
      />
      <RevokeConfirmModal
        open={modalType === FriendCardModalType.REVOKE}
        onOpenChange={handleCloseModal}
        onConfirm={() => {
          handleCloseModal();
          onRevokeFriendRequest(profileFriend);
        }}
      />
    </div>
  );
};
